import { Box, Dialog, Grid, IconButton, LinearProgress, SelectChangeEvent, Skeleton, Theme, Typography, useTheme } from "@mui/material";
import { CommonLayout } from "../../commonComponents";
import CalendarIcon from "../../../assets/icons/CalendarIcon";
import { useEffect, useMemo, useState } from "react";
import dayjs from "../../../utils/dayjs";
import { LoadingButton } from "@mui/lab";
import PeriodDropdown from "./PeriodDropdown";
import { ChartIcon, MoneyGrow, PercentageIcons } from "../../../assets/SvgIcons";
import InfoCard from "./InfoCard";
import { ErrorText, LineChartComponent } from "../../dashboard/chart/CustomeLineChart";
import CustomePieChart from "../../dashboard/chart/CustomePieChart";
import ReportGeneration from "./ReportGeneration";
import { useDispatch, useSelector } from "react-redux";
import { getInvoiceControlDashboardData } from "../../../redux-store/actions/reports";
import { GetInvoiceControlDashboardDataResponseBody, RmWiseFreezeDistributionObj } from "../../../redux-store/types/onBoarding";
import { showError } from "../../../redux-store/actions/auth";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FreezePercTable from "./FreezePercTable";
import { useHistory } from "react-router";
import { RootStateType } from "../../../redux-store/reducers";
import { isModuleAllowed } from "../../../utils/utilityFunctions";
import BreadCrum from "../../../lib/BreadCrum";

interface FreezeStatusDistData {
    data: Array<Array<string | number>>;
    labelData: Array<{ label: string; value: number; }>;
    totalValue: { label: string; value: number; }
}

const startYear = 2025
const currentYear = dayjs().year()

const YEARS: Array<{ label: string; value: string }> = []

for (let year = startYear; year <= currentYear; year++) {
    YEARS.push({
        label: `${year} - ${year + 1}`,
        value: `${year}`
    })
}

const QUARTERS = [
    { label: "April-June", value: "Q1" },
    { label: "July-September", value: "Q2" },
    { label: "October-December", value: "Q3" },
    { label: "January-March", value: "Q4" }
]

const INITIAL_CARD_VALUES = [
    { key: "rms", icon: <MoneyGrow viewBox="0 0 57 50" />, label: "Total RMs", value: "-" },
    { key: "families", icon: <MoneyGrow viewBox="0 0 57 50" />, label: "Total Families", value: "-" },
    { key: "entities", icon: <ChartIcon viewBox="0 0 57 50" />, label: "Total Billing Entities", value: "-" },
    { key: "invoiceShares", icon: <PercentageIcons viewBox="0 0 57 50" />, label: "Invoice Shared", value: "-" },
    { key: "invoiceDownloads", icon: <PercentageIcons viewBox="0 0 57 50" />, label: "Invoice Downloaded", value: "-" }
]

// Default/zeroed state for the Invoice Status Distribution pie chart.
// Covers all five invoice statuses (Freezed, Unfreezed, Not Generated,
// Non Billable, Others) so the chart renders every segment before data loads.
const INITIAL_FREEZE_STATUS_DISTRIBUTION_DATA = {
    data: [["Freezed", 0], ["Unfreezed", 0], ["Not Generated", 0], ["Non Billable", 0], ["Others", 0]],
    labelData: [
        { label: "Freezed", value: 0 },
        { label: "Unfreezed", value: 0 },
        { label: "Not Generated", value: 0 },
        { label: "Non Billable", value: 0 },
        { label: "Others", value: 0 }
    ],
    totalValue: { label: "Total", value: 0 }
}

export default function RiaDashboard(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);

    // State
    const [selectedPeriod, setSelectedPeriod] = useState<{ year: string; quarter: string; }>({ year: "2025", quarter: "Q1" })

    const [cardValues, setCardValues] = useState<Array<{ key: string; icon: JSX.Element; label: string; value: string | number; }>>(INITIAL_CARD_VALUES)
    const [freezeStatusDistData, setFreezeStatusDistData] = useState<FreezeStatusDistData>(INITIAL_FREEZE_STATUS_DISTRIBUTION_DATA)
    const [rmWiseFreezeProgressData, setRmWiseFreezeProgressData] = useState<Array<RmWiseFreezeDistributionObj>>([])

    const [isDashboardDataLoading, setDashboardDataLoading] = useState<boolean>(false)
    const [isInitialLoading, setInitialLoading] = useState<boolean>(false)

    const [isLineChartDialogOpen, setLineChartDialogOpen] = useState<boolean>(false)

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'RIA Dashboard',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        }
    ] : [])

    const getInfoCardData = (response: GetInvoiceControlDashboardDataResponseBody) => {
        setCardValues(prevState => (prevState ?? []).map(obj => {
            if (obj?.key === "rms") {
                return {
                    ...obj,
                    value: response?.invoiceControlSummaryData?.rms ?? "-"
                }
            }
            if (obj?.key === "families") {
                return {
                    ...obj,
                    value: response?.invoiceControlSummaryData?.families ?? "-"
                }
            }
            if (obj?.key === "entities") {
                return {
                    ...obj,
                    value: response?.invoiceControlSummaryData?.entities ?? ""
                }
            }
            if (obj?.key === "invoiceShares") {
                return {
                    ...obj,
                    value: response?.invoiceControlSummaryData?.invoiceShares ?? ""
                }
            }
            if (obj?.key === "invoiceDownloads") {
                return {
                    ...obj,
                    value: response?.invoiceControlSummaryData?.invoiceDownloads ?? ""
                }
            }
            return obj
        }))
    }

    const getFreezeStatusDistributionData = (response: GetInvoiceControlDashboardDataResponseBody) => {
        const totalValue = { label: "Total", value: response?.freezeStatusDistribution?.total ?? 0 }

        // Map each invoice status from the API response into the chart's
        // segment counts. Optional chaining + ?? 0 keeps the chart stable when
        // any status is missing from the response.
        const data = [
            ["Freezed", response?.freezeStatusDistribution?.freeze?.count ?? 0],
            ["Unfreezed", response?.freezeStatusDistribution?.unFreeze?.count ?? 0],
            ["Not Generated", response?.freezeStatusDistribution?.notGenerated?.count ?? 0],
            ["Non Billable", response?.freezeStatusDistribution?.nonBillable?.count ?? 0],
            ["Others", response?.freezeStatusDistribution?.others?.count ?? 0]
        ]

        // Percentage values per status, used for the chart labels.
        const labelData = [
            { label: "Freezed", value: response?.freezeStatusDistribution?.freeze?.percentage ?? 0 },
            { label: "Unfreezed", value: response?.freezeStatusDistribution?.unFreeze?.percentage ?? 0 },
            { label: "Not Generated", value: response?.freezeStatusDistribution?.notGenerated?.percentage ?? 0 },
            { label: "Non Billable", value: response?.freezeStatusDistribution?.nonBillable?.percentage ?? 0 },
            { label: "Others", value: response?.freezeStatusDistribution?.others?.percentage ?? 0 }
        ]

        setFreezeStatusDistData({ data, labelData, totalValue })
    }

    const fetchDashboardData = async ({
        year,
        quarter
    }: {
        year: string;
        quarter: string;
    }) => {
        const payload = {
            year: Number(year),
            quarter
        }

        const response = await (dispatch(getInvoiceControlDashboardData(payload)) as unknown as Promise<GetInvoiceControlDashboardDataResponseBody>);

        if (!response) {
            setCardValues(INITIAL_CARD_VALUES)
            setFreezeStatusDistData(INITIAL_FREEZE_STATUS_DISTRIBUTION_DATA)
            setRmWiseFreezeProgressData([])

            return
        }

        if (!response?.isSuccess) {
            dispatch(showError(response?.message || "Data not found"))
            setCardValues(INITIAL_CARD_VALUES)
            setFreezeStatusDistData(INITIAL_FREEZE_STATUS_DISTRIBUTION_DATA)
            setRmWiseFreezeProgressData([])

            return
        }

        if (response?.isSuccess) {
            getInfoCardData(response)
            getFreezeStatusDistributionData(response)
            setRmWiseFreezeProgressData(response?.rmWiseFreszeDistribution ?? [])
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setInitialLoading(true)

                await fetchDashboardData({ year: selectedPeriod?.year ?? "", quarter: selectedPeriod?.quarter ?? "" })
            } catch (error) {
                console.log(error)
            } finally {
                setInitialLoading(false)
            }
        }

        fetchData()
    }, [])


    const handleYearChange = (event: SelectChangeEvent) => {
        setSelectedPeriod(prevState => ({ ...prevState, year: event.target.value as string }))
    }

    const handleQuarterChange = (event: SelectChangeEvent) => {
        setSelectedPeriod(prevState => ({ ...prevState, quarter: event.target.value as string }))
    }

    const handlePeriodApply = async () => {
        try {
            setDashboardDataLoading(true)

            await fetchDashboardData({ year: selectedPeriod?.year ?? "", quarter: selectedPeriod?.quarter ?? "" })
        } catch (error) {
            console.log(error)
        } finally {
            setDashboardDataLoading(false)
        }
    }

    const handleLineChartDialogOpen = () => {
        setLineChartDialogOpen(true)
    }

    const handleLineChartDialogClose = () => {
        setLineChartDialogOpen(false)
    }

    const topSixRmWiseFreezeProgressData = useMemo(() => {

        if (!(rmWiseFreezeProgressData ?? []).length) {
            return { name: [], value: [] }
        }

        // Sort by descending percentage
        const sortedData = rmWiseFreezeProgressData.sort((a, b) => (b?.percentage ?? 0) - (a?.percentage ?? 0))

        // Take top 5 objects
        const topFiveData = sortedData.slice(0, 5);
        // Take remaining objects
        const othersData = sortedData.slice(5);

        const othersCombined = {
            rmName: "Others",
            count: othersData.reduce((sum, item) => sum + (item?.count ?? 0), 0),
            percentage: othersData.reduce((sum, item) => sum + (item?.percentage ?? 0), 0)
        };

        // const modifiedData = [...topFiveData, othersCombined]
        const modifiedData = [...topFiveData]

        return {
            name: modifiedData.map(obj => obj?.rmName ?? ""),
            value: modifiedData.map(obj => obj?.percentage ?? 0)
        }
    }, [rmWiseFreezeProgressData])

    const sortedRmWiseFreezeProgressData = useMemo(() => {
        // if (!(rmWiseFreezeProgressData ?? []).length) {
        //     return []
        // }

        // Sort by descending percentage
        const sortedData = (rmWiseFreezeProgressData ?? []).sort((a, b) => (b?.percentage ?? 0) - (a?.percentage ?? 0))

        return sortedData

        // return {
        //     name: sortedData.map(obj => obj?.rmName ?? ""),
        //     value: sortedData.map(obj => obj?.percentage ?? 0)
        // }
    }, [rmWiseFreezeProgressData])

    const freezedProgressValue = useMemo(() => {
        const data = (freezeStatusDistData?.labelData ?? []).find(obj => obj.label === "Freezed")?.value ?? 0

        return parseFloat(data.toFixed(2))
    }, [freezeStatusDistData])

    return (
        <CommonLayout>
            <>
                <Box sx={{ marginBottom: "0.5em" }}>
                    <BreadCrum array={routerPath} />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: { xs: "flex-start", md: "space-between" },
                        alignItems: { xs: "flex-start", md: "center" },
                        rowGap: "15px",
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                    >
                        RIA Dashboard
                    </Typography>

                    {/* Select Period Start */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            columnGap: "10px"
                        }}
                    >
                        <CalendarIcon
                            fillColor={(isDashboardDataLoading || isInitialLoading) ? theme.palette.grey[100] : theme.palette.primary[500]}
                        />
                        <Typography>
                            For Period of :
                        </Typography>
                        <PeriodDropdown
                            value={selectedPeriod.year}
                            onChange={handleYearChange}
                            optionsArr={YEARS}
                            disabled={isDashboardDataLoading || isInitialLoading}
                        />
                        <PeriodDropdown
                            value={selectedPeriod.quarter}
                            onChange={handleQuarterChange}
                            optionsArr={QUARTERS}
                            disabled={isDashboardDataLoading || isInitialLoading}
                        />
                        <LoadingButton
                            loadingPosition="center"
                            variant="outlined"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                backgroundColor: theme.palette.primary[500],
                                padding: "0px 20px",
                                alignSelf: "stretch",
                                "&: hover": {
                                    backgroundColor: theme.palette.primary[500],
                                },
                                "& .MuiCircularProgress-root": {
                                    color: theme.palette.customColors.aumBlue,
                                },
                                ...theme.typography.body_500,
                                fontWeight: 550
                            })}
                            onClick={handlePeriodApply}
                            disabled={isInitialLoading}
                            loading={isDashboardDataLoading}
                        >
                            Apply
                        </LoadingButton>
                    </Box>
                    {/* Select Period End */}

                </Box>
                {/* Info cards Start */}
                <Grid container spacing={2} sx={{ alignItems: "stretch", marginTop: "30px" }}>
                    {cardValues.map(card => (
                        <InfoCard
                            key={card.key}
                            icon={card.icon}
                            label={card.label}
                            value={card.value}
                            isLoading={isDashboardDataLoading || isInitialLoading}
                        />
                    ))}
                </Grid>
                {/* Info cards End */}

                <Grid container spacing={2} sx={{ marginTop: "25px", alignItems: "stretch" }}>
                    <Grid item xs={12} lg={6}>
                        <Box
                            sx={{
                                height: '100%',
                                padding: '25px',
                                borderRadius: '15px',
                                background: theme.palette.common.white,
                                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                                display: "flex",
                                flexDirection: "column",
                                rowGap: "15px"
                            }}
                        >
                            {/* Renamed from "Freeze Status Distribution": the chart now shows all invoice statuses, not just freeze/unfreeze */}
                            <Typography variant='h2'>Invoice Status Distribution</Typography>
                            <Box
                                sx={{
                                    flex: 1,
                                    paddingTop: "20px",
                                    paddingBottom: "20px",
                                    display: 'flex',
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {(freezeStatusDistData?.data ?? []).length ? (
                                    <CustomePieChart
                                        data={freezeStatusDistData?.data ?? []}
                                        isLoading={isDashboardDataLoading || isInitialLoading}
                                        labelData={freezeStatusDistData?.labelData ?? []}
                                        totalValue={freezeStatusDistData?.totalValue ?? INITIAL_FREEZE_STATUS_DISTRIBUTION_DATA.totalValue}
                                        showSlider
                                        showOriginalTotalValue
                                    />
                                ) : <ErrorText />}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    rowGap: "10px"
                                }}
                            >
                                {(isDashboardDataLoading || isInitialLoading) && (
                                    <Skeleton variant="rounded" height="20px" />
                                )}
                                {!(isDashboardDataLoading || isInitialLoading) && (
                                    <>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography variant="body_600">Freezed Progress</Typography>
                                            <Typography variant="body_600">{freezedProgressValue}%</Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={freezedProgressValue}
                                            aria-label="Freezed Progress"
                                            color="success"
                                        />
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Box
                            sx={{
                                height: "100%",
                                padding: '25px',
                                borderRadius: '15px',
                                background: theme.palette.common.white,
                                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                                display: "flex",
                                flexDirection: "column",
                                rowGap: "30px"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        columnGap: "8px",
                                        alignItems: "center"
                                    }}
                                >
                                    <Typography variant='h2'>Freeze % by RM</Typography>
                                    <IconButton
                                        sx={{
                                            padding: "0px",
                                            cursor: 'pointer',
                                            "&: disabled": {
                                                opacity: 0.5
                                            }
                                        }}
                                        disabled={isDashboardDataLoading || isInitialLoading}
                                        onClick={handleLineChartDialogOpen}
                                        title="View All"
                                    >
                                        <KeyboardArrowRightIcon sx={{ color: 'text.primary' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <LineChartComponent
                                    isLoading={isDashboardDataLoading || isInitialLoading}
                                    data={topSixRmWiseFreezeProgressData}
                                    showTooltip={false}
                                    showCurrency={false}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <ReportGeneration />

                {/* RM-wise Freeze Table Dialog Start */}

                <Dialog
                    onClose={handleLineChartDialogClose}
                    open={isLineChartDialogOpen}
                    maxWidth='lg'
                    fullWidth
                    sx={(theme: Theme) => ({
                        '& .MuiPaper-root': {
                            borderRadius: '25px',
                            backgroundColor: theme.palette.common.white,
                            padding: { xs: "20px 15px 10px 15px", sm: "25px 30px 15px 30px" },
                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                            maxHeight: "80%",
                            minHeight: "200px"
                        },
                    })}
                >
                    <Typography
                        variant='h2'
                        sx={{ marginBottom: "40px" }}
                    >
                        RM-wise Freeze Unfreeze Data
                    </Typography>
                    <FreezePercTable
                        tableData={sortedRmWiseFreezeProgressData ?? []}
                    />
                </Dialog>

                {/* RM-wise Freeze Table Dialog End */}
            </>
        </CommonLayout>
    )
}